// DispatchDeck — Reminders (TASK-1550)
// ============================================================================
// `window.Reminders` — a note-to-self attached to a PERSON, VEHICLE or JOB,
// delivered as an in-app banner (this file) plus, if the box is ticked, a text
// to the creator's OWN phone (server: functions/src/services/opsReminders.js,
// fired by the 5-minute processScheduledReminders cron).
//
// Data: tenants/{tid}/reminders — see the service header for the shape.
// Writes: ONLY the `manageReminder` callable (rules are read-own/write:false),
// which binds the tenant from the auth claim, gates on the module that owns the
// record, and writes the audit row in the same transaction.
//
// WHO SEES WHAT (decided for v1): the banner and every list show the CREATOR
// their own reminders only. No assignees. A reminder is a note to self — the
// cheapest possible mistake (TASK-1418 roadmap), and it stays that way: nobody
// else is ever shown or texted one. Sharing a reminder with crew is a separate,
// deliberate feature (it would text people who never asked), not a toggle here.
//
// ACCESS — mirrors the server + rules (affordance layer only; rules enforce):
//   person  -> roster | hr        vehicle -> roster | maintenance
//   job     -> dispatch           owner (tenants.ownerId) -> all three
// Driver-PIN / link sessions never see reminders.
//
// LOADING (Pitfall #1): imported ONCE by app.js. Hosts (roster.js, hr.js, maintenance.js, dispatch.js)
// call `window.Reminders?.mount(...)` at call time — never an import.
//
// TASK-9569 — FINDABLE. No sidebar entry any more: a bell on every Roster person
// card, Roster vehicle card and Dispatch job card (`window.Reminders.bellHtml`,
// one shared component). Outline bell = none; filled amber bell + a short due
// label = one or more open. Tap -> that record's reminders (or straight to Add).
// The full list is reached from a bell, the due banner, or Settings. Each login
// can hide the bells (Settings -> "Show reminder bells"; users/{uid}
// .reminderBellsHidden — a presentational pref on the caller's own profile doc).
// Hiding never deletes or silences anything: the banner + texts still fire.
//
// TASK-10018 — SHARED OFFICE NOTICES. A text DD could not act on (a driver
// with no Dispatch texting in a job, a change, an undo or a clock fix) is ONE
// row for the whole office (shared:true, no ownerUid — written by the server,
// smsSenderAccess.handleRefusal), not one per person. Logins that can use
// Dispatch listen on `openFor array-contains uid`. It shows in this same
// banner with the WHOLE message, who sent it and when, and:
//   • Make this a job — opens New Job with the text in Notes and in the Job
//     Builder box (never auto-run). Only for a login with Dispatch that can
//     create jobs (owner via ownerId, or the dispatch module; not read-only,
//     not view-and-upload).
//   • Text thread — the last messages of that SMS thread, for a login the
//     smsInboxThreads rule lets read it (owner/admin member).
//   • Snooze 1h (this login only) · Done (closes it for EVERYONE; the others
//     see "handled by <name>").
// The message is untrusted SMS text: everything is escaped (or textContent),
// no inline handler carries it, and it is never run or sent anywhere.
// ============================================================================

import {
  auth,
  db,
  functions,
  httpsCallable,
  onAuthStateChanged,
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  orderBy,
  limit,
  updateDoc,
} from './firebase-config.js?v=1868';
import './utils/reminderTime.js?v=1989551';

(function () {
  'use strict';

  const TYPE_MODULES = {
    person: ['roster', 'hr'],
    vehicle: ['roster', 'maintenance'],
    job: ['dispatch'],
  };
  const TYPE_NOUN = { person: 'person', vehicle: 'vehicle', job: 'job' };
  const TYPE_ICON = { person: 'person', vehicle: 'local_shipping', job: 'work' };
  const INACTIVE = ['suspended', 'removed', 'revoked', 'inactive', 'pending'];
  const TITLE_MAX = 140;

  const S = {
    key: null,              // `${tenant}|${uid}` the listeners belong to
    unsubs: [],
    byType: { person: [], vehicle: [], job: [] },
    types: [],
    mounts: new Map(),      // containerId -> { type, id, label }
    dismissed: new Set(),   // reminder ids whose banner the user closed this session
    tick: null,
    modal: null,            // { mode, type, id, label, reminderId }
    busy: false,
    bellsHidden: false,     // TASK-9569 — this login's "Show reminder bells" pref (false = shown)
    prefKey: null,          // uid whose pref is loaded
    prefRev: 0,             // bumped on every local change (stale-read guard)
    listFilter: null,       // { type, id, label } when the list modal shows ONE record's reminders
    shared: [],             // TASK-10018 — open shared office notices this login is in
    sharedLoaded: false,
  };
  const SHARED_HEAD = { job_request: 'Job request', job_change: 'Job change', undo: 'Undo request', clock_change: 'Clock change' };
  const TEXTED_JOB_WAIT_MS = 10000;

  const RT = () => window.ReminderTime;
  const esc = (s) => (window.escapeHtml ? window.escapeHtml(s) : String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;'));

  function tenantId() {
    const t = window.AppState?.tenant;
    return t === 'demo-tenant' ? 'demo' : (t || null);
  }
  function isDemo() { return !!window.AppState?.isDemo || tenantId() === 'demo'; }
  function zone() {
    const s = window.AppState?.settings || window.AppState?.tenantData?.settings || {};
    return RT().resolveZone(s.timezone);
  }
  function isDriverContext() {
    return !!(window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.accessToken);
  }

  /** Can the signed-in user use reminders on this record type? */
  function canUseType(type) {
    if (!TYPE_MODULES[type]) return false;
    if (isDriverContext()) return false;
    const user = window.AppState?.user || auth.currentUser;
    if (!user) return false;
    if (isDemo()) return true;
    if (user.isAnonymous) return false;
    if (window.AppState?.tenantData?.ownerId && window.AppState.tenantData.ownerId === user.uid) return true;
    const ma = window.AppState?.memberAccess;
    if (!ma || !Array.isArray(ma.modules)) return false;
    if (INACTIVE.includes(ma.status)) return false;
    if (ma.readOnly === true) return false;
    return ma.modules.some((m) => TYPE_MODULES[type].includes(m));
  }

  function tsMs(v) {
    if (!v) return NaN;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (v instanceof Date) return v.getTime();
    return Number(v);
  }

  function all() {
    return [...S.byType.person, ...S.byType.vehicle, ...S.byType.job]
      .sort((a, b) => a.dueMs - b.dueMs);
  }
  function dueNow() { const now = Date.now(); return all().filter((r) => r.dueMs <= now); }
  function find(id) { return all().find((r) => r.id === id) || null; }
  // TASK-10018 — shared notices: open for me, and not snoozed by ME.
  function sharedDue() { const now = Date.now(); return S.shared.filter((n) => !(n.snoozeMs > now)); }
  function findShared(id) { return S.shared.find((n) => n.id === id) || null; }

  /** Shared notice row -> what the UI needs. Strings stay raw here; every
   *  render escapes them (they come off an SMS). */
  function sharedFromDoc(id, x, uid) {
    const sn = x.snoozedUntil && typeof x.snoozedUntil === 'object' ? x.snoozedUntil[uid] : null;
    return {
      id,
      shared: true,
      kind: SHARED_HEAD[x.kind] ? x.kind : 'job_request',
      title: String(x.title || ''),
      sender: String(x.senderName || x.attachedLabel || 'A crew member'),
      message: String(x.message || ''),
      truncated: x.messageTruncated === true,
      receivedMs: tsMs(x.receivedAt || x.createdAt),
      threadId: typeof x.threadId === 'string' ? x.threadId : null,
      snoozeMs: tsMs(sn),
    };
  }

  /** May this login make a job from a text? Owner via ownerId, or the dispatch
   *  module on a live login that can create (not read-only, not view-and-upload).
   *  Affordance only: the job form's Save is still gated by firestore.rules. */
  function canMakeJob() {
    if (isDemo() || isDriverContext()) return false;
    const user = window.AppState?.user || auth.currentUser;
    if (!user || user.isAnonymous) return false;
    const td = window.AppState?.tenantData || {};
    if (td.complianceOnly === true) return false;
    if (td.ownerId && td.ownerId === user.uid) return true;
    const ma = window.AppState?.memberAccess;
    if (!ma || !Array.isArray(ma.modules)) return false;
    if (INACTIVE.includes(ma.status) || ma.readOnly === true || ma.viewUploadOnly === true) return false;
    return ma.modules.includes('dispatch');
  }

  /** Mirrors the smsInboxThreads read rule (isTenantAdmin: an owner/admin
   *  members doc). Affordance only — the rule decides. */
  function canReadThread() {
    if (isDemo() || isDriverContext()) return false;
    const ma = window.AppState?.memberAccess;
    return !!(ma && (ma.role === 'owner' || ma.role === 'admin') && !INACTIVE.includes(ma.status));
  }

  // --------------------------------------------------------------------------
  // Listeners
  // --------------------------------------------------------------------------
  function start() {
    const user = auth.currentUser;
    const tid = tenantId();
    if (!user || !tid || !RT()) { stop(); return; }
    const types = Object.keys(TYPE_MODULES).filter(canUseType);
    const key = `${tid}|${user.uid}|${types.join(',')}`;
    if (S.key === key) return;
    stop();
    S.key = key;
    S.types = types;
    injectCss();
    loadPref(user);
    refreshBells();
    paintSettingsRow();
    if (!types.length) return;
    const col = collection(db, 'tenants', tid, 'reminders');
    for (const type of types) {
      // ownerUid + attachedType are BOTH required: the read rule gates on the
      // module for attachedType, so a list query must pin it (TASK-1550 rules).
      const q = query(col, where('ownerUid', '==', user.uid), where('attachedType', '==', type), where('status', '==', 'open'));
      const unsub = onSnapshot(q, (snap) => {
        S.byType[type] = snap.docs.map((d) => {
          const x = d.data() || {};
          return {
            id: d.id,
            type,
            attachedId: x.attachedId,
            label: x.attachedLabel || '',
            title: x.title || '',
            dueMs: tsMs(x.dueAt),
            wantsSms: x.wantsSms === true,
            smsStatus: x.smsStatus || null,
            smsDetail: x.smsDetail || null,
          };
        });
        renderAll();
      }, (err) => {
        console.warn('[Reminders] listener error (' + type + '):', err?.code || err?.message || err);
      });
      S.unsubs.push(unsub);
    }
    // TASK-10018 — shared office notices. Read rule: in openFor/audienceUids AND
    // can use Dispatch (remCanType('job')), so only a login that holds 'job'.
    if (types.includes('job') && !isDemo()) {
      const uid = user.uid;
      const qs = query(col, where('openFor', 'array-contains', uid));
      const unsubShared = onSnapshot(qs, (snap) => {
        const before = new Map(S.shared.map((n) => [n.id, n]));
        S.shared = snap.docs.map((d) => sharedFromDoc(d.id, d.data() || {}, uid))
          .sort((a, b) => (a.receivedMs || 0) - (b.receivedMs || 0));
        const firstLoad = !S.sharedLoaded;
        S.sharedLoaded = true;
        renderAll();
        if (firstLoad) return;
        // Gone from MY open list: someone (maybe me) marked it Done. Say who.
        for (const [id, n] of before) {
          if (S.shared.some((x) => x.id === id)) continue;
          S.dismissed.delete(id);
          announceHandled(tid, id, n, uid);
        }
      }, (err) => {
        console.warn('[Reminders] shared notice listener error:', err?.code || err?.message || err);
      });
      S.unsubs.push(unsubShared);
    }
    // TASK-9569 — the bells' "Due" / time labels age with the clock too.
    S.tick = setInterval(() => { renderBanner(); refreshBells(); }, 30000);
  }

  function stop() {
    S.unsubs.forEach((u) => { try { u(); } catch (e) { /* ignore */ } });
    S.unsubs = [];
    S.byType = { person: [], vehicle: [], job: [] };
    S.shared = [];
    S.sharedLoaded = false;
    S.types = [];
    S.key = null;
    if (S.tick) { clearInterval(S.tick); S.tick = null; }
    S.dismissed.clear();
    removeBanner();
    refreshBells();
    paintSettingsRow();
    S.mounts.forEach((_, id) => { const el = document.getElementById(id); if (el) el.innerHTML = ''; });
  }

  onAuthStateChanged(auth, (u) => {
    if (!u) { stop(); return; }
    if (S.key && !S.key.includes('|' + u.uid + '|')) stop();
  });

  // --------------------------------------------------------------------------
  // Calls
  // --------------------------------------------------------------------------
  const _call = httpsCallable(functions, 'manageReminder');
  async function call(payload) {
    const res = await _call({ ...payload, tenantId: tenantId() });
    return res?.data || {};
  }
  function friendly(err) {
    const m = err?.message || '';
    if (!m || /internal/i.test(err?.code || '')) return 'Could not save the reminder. Try again.';
    return m.replace(/^FirebaseError:\s*/, '');
  }
  const toast = (m, t) => (window.showToast ? window.showToast(m, t) : console.log('[Reminders]', m));

  // --------------------------------------------------------------------------
  // Record section (person / vehicle / job detail)
  // --------------------------------------------------------------------------
  function mount(containerId, opts) {
    const o = opts || {};
    if (!containerId || !TYPE_MODULES[o.type] || !o.id) return;
    S.mounts.set(containerId, { type: o.type, id: String(o.id), label: String(o.label || '') });
    start();
    renderMount(containerId);
  }

  function mountAppend(parentEl, opts) {
    if (!parentEl || !opts) return;
    const id = `rem-slot-${opts.type}`;
    let el = document.getElementById(id);
    if (!el || !parentEl.contains(el)) {
      el?.remove();
      el = document.createElement('div');
      el.id = id;
      parentEl.appendChild(el);
    }
    mount(id, opts);
  }

  function unmount(containerId) {
    S.mounts.delete(containerId);
    const el = document.getElementById(containerId);
    if (el) el.innerHTML = '';
  }

  function itemHtml(r, now, showAbout) {
    const tz = zone();
    const overdue = r.dueMs <= now;
    const due = RT().formatDue(r.dueMs, tz, now);
    let sms = '';
    if (r.wantsSms) {
      const map = {
        sent: 'Texted', no_phone: 'No phone on your profile', muted: 'Texts muted on your profile',
        opted_out: 'Your number replied STOP', refused: 'Text not sent (SMS credits)', failed: 'Text failed',
        skipped_access: 'Text not sent', demo_no_sms: 'Texts are off in the demo',
      };
      // TASK-10027 muted-tenant UI hints (Chris 10/22): a pending text waits out a company
      // pause (opsReminders defers it); say so instead of "Text + banner". TASK-10025: a
      // HARD no-contact company's text is refused for good (opsReminders terminal
      // 'refused'), so it is not "for now" and not an SMS-credits refusal.
      const _sm = window.getTenantSendMode?.() || { mode: 'live' };
      sms = (r.smsStatus === 'refused' && _sm.textsRefused) ? 'Banner only — texts are off for this company'
        : r.smsStatus ? (map[r.smsStatus] || 'Text not sent')
          : _sm.textsRefused ? 'Banner only — texts are off for this company'
            : _sm.textsHeld ? 'Banner only for now — texts paused'
              : 'Text + banner';
      sms = `<span class="rem-sms-chip" title="${esc(r.smsDetail || sms)}"><span class="material-icons">sms</span>${esc(sms)}</span>`;
    }
    const about = showAbout
      ? `<div class="rem-about-line"><span class="material-icons">${TYPE_ICON[r.type]}</span>${esc(r.label || TYPE_NOUN[r.type])}</div>`
      : '';
    return `<div class="rem-item${overdue ? ' rem-overdue' : ''}" data-rem-id="${esc(r.id)}">
      <div class="rem-item-main">
        <div class="rem-item-title">${esc(r.title)}</div>
        ${about}
        <div class="rem-item-meta"><span class="material-icons">${overdue ? 'alarm_on' : 'schedule'}</span>${esc(due)}${sms}</div>
      </div>
      <div class="rem-item-actions">
        <button type="button" class="btn btn-sm btn-secondary" data-rem-action="snooze" data-rem-id="${esc(r.id)}" title="Snooze"><span class="material-icons">snooze</span></button>
        <button type="button" class="btn btn-sm btn-secondary" data-rem-action="done" data-rem-id="${esc(r.id)}" title="Done"><span class="material-icons">check</span></button>
        <button type="button" class="btn btn-sm btn-secondary" data-rem-action="edit" data-rem-id="${esc(r.id)}" title="Edit"><span class="material-icons">edit</span></button>
        <button type="button" class="btn btn-sm btn-secondary" data-rem-action="delete" data-rem-id="${esc(r.id)}" title="Delete"><span class="material-icons">delete</span></button>
      </div>
    </div>`;
  }

  function renderMount(containerId) {
    const m = S.mounts.get(containerId);
    const el = document.getElementById(containerId);
    if (!m || !el) return;
    if (!canUseType(m.type)) { el.innerHTML = ''; return; }
    const now = Date.now();
    const items = (S.byType[m.type] || []).filter((r) => r.attachedId === m.id).sort((a, b) => a.dueMs - b.dueMs);
    el.innerHTML = `<div class="rem-section" data-rem-mount="${esc(containerId)}">
      <div class="rem-section-head">
        <span class="rem-section-title"><span class="material-icons">alarm</span>My reminders</span>
        <button type="button" class="btn btn-sm btn-secondary rem-add-btn" data-trailguide="reminders" data-rem-action="add" data-rem-mount="${esc(containerId)}">
          <span class="material-icons">add_alarm</span> Add reminder
        </button>
      </div>
      <div class="rem-list">${items.length
        ? items.map((r) => itemHtml(r, now, false)).join('')
        : `<div class="rem-empty">No reminders on this ${TYPE_NOUN[m.type]}. Only you see your reminders.</div>`}</div>
    </div>`;
    el.onclick = onListClick;
  }

  function renderAll() {
    S.mounts.forEach((_, id) => {
      if (!document.getElementById(id)) { S.mounts.delete(id); return; }
      renderMount(id);
    });
    renderBanner();
    renderMyList();
    refreshBells();
    paintSettingsRow();
  }

  function onListClick(ev) {
    const btn = ev.target.closest('[data-rem-action]');
    if (!btn) return;
    ev.preventDefault();
    ev.stopPropagation();
    const action = btn.getAttribute('data-rem-action');
    const id = btn.getAttribute('data-rem-id');
    if (action === 'add') {
      const m = S.mounts.get(btn.getAttribute('data-rem-mount'));
      if (m) openCreate(m.type, m.id, m.label);
      return;
    }
    if (action === 'add-filtered') {
      const f = S.listFilter;
      if (f) openCreate(f.type, f.id, f.label);
      return;
    }
    if (action === 'show-all') { openMyReminders(); return; }
    if (action.startsWith('shared-')) { onSharedAction(action, id); return; }
    const r = find(id);
    if (!r) return;
    if (action === 'snooze') openSnooze(r);
    else if (action === 'edit') openEdit(r);
    else if (action === 'done') complete(r);
    else if (action === 'delete') remove(r);
    else if (action === 'snooze1h') snoozeTo(r, RT().quickPick('in1h', Date.now(), zone()));
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------
  async function complete(r) {
    try {
      await call({ op: 'complete', id: r.id });
      S.dismissed.delete(r.id);
      toast('Reminder done.', 'success');
    } catch (e) { toast(friendly(e), 'error'); }
  }

  async function remove(r) {
    const ok = window.showConfirm
      ? await window.showConfirm('Delete reminder?', `"${r.title}" will be removed. This can't be undone.`, { confirmText: 'Delete', danger: true, icon: 'delete' })
      : window.confirm('Delete this reminder?');
    if (!ok) return;
    try {
      await call({ op: 'delete', id: r.id });
      toast('Reminder deleted.', 'success');
    } catch (e) { toast(friendly(e), 'error'); }
  }

  async function snoozeTo(r, ms) {
    if (!Number.isFinite(ms)) return;
    try {
      await call({ op: 'snooze', id: r.id, dueAtMs: ms });
      S.dismissed.delete(r.id);
      toast(`Snoozed until ${RT().formatDue(ms, zone(), Date.now())}.`, 'success');
    } catch (e) { toast(friendly(e), 'error'); }
  }

  // --------------------------------------------------------------------------
  // TASK-10018 — shared office notice actions
  // --------------------------------------------------------------------------
  function sharedShort(n) { return `${SHARED_HEAD[n.kind] || 'Text'} from ${n.sender}`; }
  const _sharedBusy = new Set();

  async function sharedDone(n) {
    if (!n || _sharedBusy.has(n.id)) return;
    _sharedBusy.add(n.id);
    try {
      const res = await call({ op: 'shared_done', id: n.id });
      S.dismissed.delete(n.id);
      if (res.noop && !res.handledByMe) toast(`${sharedShort(n)} was already handled by ${res.handledByName || 'someone else'}.`, 'info');
      else toast('Done — cleared for everyone in the office.', 'success');
    } catch (e) { toast(friendly(e), 'error'); } finally { _sharedBusy.delete(n.id); }
  }

  async function sharedSnooze(n) {
    if (!n || _sharedBusy.has(n.id)) return;
    const ms = RT().quickPick('in1h', Date.now(), zone());
    if (!Number.isFinite(ms)) return;
    _sharedBusy.add(n.id);
    try {
      await call({ op: 'shared_snooze', id: n.id, dueAtMs: ms });
      // Hide it for this login right away; the snapshot confirms.
      n.snoozeMs = ms;
      renderAll();
      toast(`Snoozed for you until ${RT().formatDue(ms, zone(), Date.now())}. Others still see it.`, 'success');
    } catch (e) { toast(friendly(e), 'error'); } finally { _sharedBusy.delete(n.id); }
  }

  // Left MY open list (openFor emptied). Read the row (the audience may still
  // get it) and say who handled it — unless it was me.
  function announceHandled(tid, id, n, uid) {
    getDoc(doc(db, 'tenants', tid, 'reminders', id)).then((snap) => {
      if (!snap.exists()) return;
      const x = snap.data() || {};
      if (x.status !== 'done' || !x.handledByUid || x.handledByUid === uid) return;
      toast(`${sharedShort(n)} — handled by ${String(x.handledByName || 'someone in the office')}.`, 'info');
    }).catch(() => { /* lost access or offline: nothing to say */ });
  }

  // "Make this a job": the ordinary New Job form, with the text in Notes and
  // (AI pack) in the Job Builder box. Nothing is parsed, sent or saved here —
  // the person reads it, fixes it, and presses Save under the normal rules.
  async function makeJobFromNotice(n) {
    if (!n) return;
    if (!canMakeJob()) { toast('Making a job needs the Dispatch module on your login (or the account owner).', 'info'); return; }
    closeAllLists();
    const ready = () => !!(document.getElementById('form-job') && document.getElementById('job-notes') && window.DispatchModule?.showAddJob);
    if (!ready() || window.AppState?.currentView !== 'dispatch') {
      if (typeof window.navigateToView === 'function') window.navigateToView('dispatch');
      else window.location.hash = '#/dispatch';
    }
    const until = Date.now() + TEXTED_JOB_WAIT_MS;
    while (!ready()) {
      if (Date.now() > until) { toast('Dispatch did not open. Open Dispatch and tap New Job.', 'warning'); return; }
      await new Promise((r) => setTimeout(r, 200));
    }
    try {
      window.DispatchModule.showAddJob();
      const t = document.getElementById('modal-job-title');
      if (t) t.textContent = `New Job — from ${n.sender}'s text`;
      const notes = document.getElementById('job-notes');
      if (notes) {
        notes.value = n.message;
        notes.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (typeof window.hasAiPack === 'function' && window.hasAiPack()) {
        document.getElementById('ai-prompt-section')?.classList.remove('hidden');
        const box = document.getElementById('ai-prompt-input');
        if (box) box.value = n.message;
      }
    } catch (e) {
      console.warn('[Reminders] make job from text failed:', e?.message || e);
      toast('Could not open New Job. Open Dispatch and tap New Job.', 'error');
    }
  }

  function closeAllLists() {
    const m = document.getElementById('my-reminders-modal');
    if (m) { m.classList.add('hidden'); S.listFilter = null; }
  }

  // The SMS thread (last 20 inbound/outbound), read under the thread's own
  // rule. Built with textContent only: every body came off a phone.
  async function openThread(n) {
    if (!n || !n.threadId) return;
    const tid = tenantId();
    let m = document.getElementById('rem-thread-modal');
    if (!m) {
      m = document.createElement('div');
      m.id = 'rem-thread-modal';
      m.className = 'modal hidden';
      m.setAttribute('role', 'dialog');
      m.setAttribute('aria-modal', 'true');
      m.setAttribute('aria-labelledby', 'rem-thread-title');
      m.innerHTML = `<div class="modal-content rem-my-content">
        <div class="modal-header">
          <h3 id="rem-thread-title">Text thread</h3>
          <button type="button" class="btn-icon" data-rem-close aria-label="Close"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body"><div id="rem-thread-body"></div></div>
      </div>`;
      document.body.appendChild(m);
      m.addEventListener('click', (ev) => {
        if (ev.target === m || ev.target.closest('[data-rem-close]')) m.classList.add('hidden');
      });
    }
    document.getElementById('rem-thread-title').textContent = `Text thread — ${n.sender}`;
    const body = document.getElementById('rem-thread-body');
    body.textContent = 'Loading…';
    m.classList.remove('hidden');
    try {
      const q = query(collection(db, 'tenants', tid, 'smsInboxThreads', n.threadId, 'messages'), orderBy('createdAt', 'desc'), limit(20));
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => d.data() || {})
        .filter((x) => x.direction === 'inbound' || x.direction === 'outbound')
        .reverse();
      body.textContent = '';
      if (!rows.length) { body.textContent = 'No messages in this thread.'; return; }
      const now = Date.now();
      for (const x of rows) {
        const row = document.createElement('div');
        row.className = 'rem-thread-row' + (x.direction === 'inbound' ? ' rem-thread-in' : ' rem-thread-out');
        const who = document.createElement('div');
        who.className = 'rem-thread-who';
        const ms = tsMs(x.createdAt);
        who.textContent = (x.direction === 'inbound' ? n.sender : 'DispatchDeck') + (Number.isFinite(ms) ? ' · ' + RT().formatDue(ms, zone(), now) : '');
        const txt = document.createElement('div');
        txt.className = 'rem-thread-text';
        txt.textContent = String(x.body || '');
        row.appendChild(who);
        row.appendChild(txt);
        body.appendChild(row);
      }
    } catch (e) {
      body.textContent = e?.code === 'permission-denied'
        ? 'Only an admin login can open the text thread. The whole message is on the notice.'
        : 'Could not load the thread. Try again.';
    }
  }

  // One shared notice, as a card. Every string escaped; buttons carry only the
  // row id in a data- attribute (no inline handler, no SMS text in JS).
  function sharedCardHtml(n, now, attr) {
    const when = Number.isFinite(n.receivedMs) ? RT().formatDue(n.receivedMs, zone(), now) : '';
    const a = (act) => `${attr}="${act}" data-rem-id="${esc(n.id)}"`;
    const job = n.kind === 'job_request' && canMakeJob()
      ? `<button type="button" class="rb-btn rem-shared-primary" ${a('shared-job')}>Make this a job</button>` : '';
    const thread = n.threadId && canReadThread()
      ? `<button type="button" class="rb-btn" ${a('shared-thread')}>Text thread</button>` : '';
    return `<div class="rem-shared-card" data-rem-shared="${esc(n.id)}">
        <div class="rem-shared-title">${esc(n.title)}</div>
        <div class="rem-shared-msg">${esc(n.message)}</div>
        <div class="rem-shared-meta">From ${esc(n.sender)}${when ? ` · ${esc(when)}` : ''}${n.truncated ? ' · long message, first 1,600 characters shown' : ''}</div>
        <div class="rem-shared-actions">${job}${thread}
          <button type="button" class="rb-btn" ${a('shared-snooze')}>Snooze 1h</button>
          <button type="button" class="rb-btn" ${a('shared-done')}>Done</button>
        </div>
      </div>`;
  }

  function onSharedAction(action, id) {
    const n = findShared(id);
    if (!n) return false;
    if (action === 'shared-job') makeJobFromNotice(n);
    else if (action === 'shared-thread') openThread(n);
    else if (action === 'shared-snooze') sharedSnooze(n);
    else if (action === 'shared-done') sharedDone(n);
    else return false;
    return true;
  }

  // --------------------------------------------------------------------------
  // Modal (create / edit / snooze)
  // --------------------------------------------------------------------------
  function ensureModal() {
    let m = document.getElementById('reminder-modal');
    if (m) return m;
    m = document.createElement('div');
    m.id = 'reminder-modal';
    m.className = 'modal hidden';
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.setAttribute('aria-labelledby', 'reminder-modal-title');
    m.innerHTML = `<div class="modal-content rem-modal-content">
      <div class="modal-header">
        <h3 id="reminder-modal-title">Add reminder</h3>
        <button type="button" class="btn-icon" data-rem-close aria-label="Close"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body">
        <div class="rem-modal-about" id="rem-modal-about"></div>
        <div class="form-group" id="rem-title-group">
          <label for="rem-title">Remind me to</label>
          <input type="text" id="rem-title" maxlength="${TITLE_MAX}" autocomplete="off" placeholder="e.g. Unmute him Sunday night" aria-describedby="rem-pii-warning">
          <div class="rem-hint rem-pii-warning" id="rem-pii-warning"><span class="material-icons rem-inline-icon" aria-hidden="true">privacy_tip</span> No personal info here — no SSNs, birth dates, medical or drug-test results. Reminders can be texted.</div>
        </div>
        <div class="rem-quick" id="rem-quick"></div>
        <div class="rem-when">
          <div class="form-group"><label for="rem-date">Date</label><input type="date" id="rem-date"></div>
          <div class="form-group"><label for="rem-time">Time</label><input type="time" id="rem-time" step="300"></div>
        </div>
        <div class="rem-hint" id="rem-tz-hint"></div>
        <div id="rem-sms-group">
          <label class="rem-check"><input type="checkbox" id="rem-sms"> Also text ME (my own phone) when it's due</label>
          <div class="rem-hint">Goes to the phone on your own roster profile — nobody else. Uses 1 SMS credit.</div>
          <div data-tenant-send-notice="reminder" style="display:none;margin-top:6px;"></div>
        </div>
        <div class="rem-error" id="rem-error" role="alert"></div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-rem-close>Cancel</button>
        <button type="button" class="btn btn-primary" id="rem-save">Save</button>
      </div>
    </div>`;
    document.body.appendChild(m);
    m.addEventListener('click', (ev) => {
      if (ev.target === m || ev.target.closest('[data-rem-close]')) { closeModal(); return; }
      const q = ev.target.closest('[data-rem-quick]');
      if (q) {
        const ms = RT().quickPick(q.getAttribute('data-rem-quick'), Date.now(), zone());
        if (Number.isFinite(ms)) {
          const v = RT().toInputs(ms, zone());
          document.getElementById('rem-date').value = v.date;
          document.getElementById('rem-time').value = v.time;
          if (S.modal?.mode === 'snooze') save();
        }
      }
    });
    m.querySelector('#rem-save').addEventListener('click', save);
    m.querySelector('#rem-title').addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); save(); } });
    // Chris 2026-10-17: warn, never block. A note that looks like it holds an
    // SSN (nine digits, dashes/spaces optional) turns the warning red.
    m.querySelector('#rem-title').addEventListener('input', paintPiiWarning);
    return m;
  }

  const SSN_LIKE = /(^|[^0-9])[0-9]{3}[- ]?[0-9]{2}[- ]?[0-9]{4}([^0-9]|$)/;
  function looksLikeSsn(text) { return SSN_LIKE.test(String(text || '')); }
  function paintPiiWarning() {
    const w = document.getElementById('rem-pii-warning');
    const t = document.getElementById('rem-title');
    if (w && t) w.classList.toggle('rem-pii-hit', looksLikeSsn(t.value));
  }

  function quickButtons() {
    const now = Date.now();
    const tz = zone();
    const picks = [['in1h', 'In 1 hour'], ['in3h', 'In 3 hours'], ['tonight', 'Tonight 8 PM'], ['tomorrow', 'Tomorrow 8 AM']];
    return picks
      .filter(([k]) => Number.isFinite(RT().quickPick(k, now, tz)))
      .map(([k, label]) => `<button type="button" class="btn btn-sm btn-secondary" data-rem-quick="${k}">${label}</button>`)
      .join('');
  }

  function openModal(state, title, dueMs, wantsSms) {
    const m = ensureModal();
    S.modal = state;
    const tz = zone();
    const isSnooze = state.mode === 'snooze';
    document.getElementById('reminder-modal-title').textContent =
      state.mode === 'create' ? 'Add reminder' : (isSnooze ? 'Snooze until…' : 'Edit reminder');
    document.getElementById('rem-modal-about').innerHTML =
      `<span class="material-icons">${TYPE_ICON[state.type]}</span><span>${esc(state.label || TYPE_NOUN[state.type])}</span>`
      + (isSnooze ? `<span class="rem-modal-about-title">— ${esc(title)}</span>` : '');
    document.getElementById('rem-title-group').style.display = isSnooze ? 'none' : '';
    document.getElementById('rem-title').value = isSnooze ? '' : (title || '');
    paintPiiWarning();
    document.getElementById('rem-quick').innerHTML = quickButtons();
    const v = RT().toInputs(dueMs, tz);
    document.getElementById('rem-date').value = v.date;
    document.getElementById('rem-time').value = v.time;
    document.getElementById('rem-tz-hint').textContent =
      `Company time (${RT().zoneLabel(tz, dueMs)}${tz !== RT().DEFAULT_TZ ? ' — ' + tz : ''}).`;
    const smsGroup = document.getElementById('rem-sms-group');
    smsGroup.style.display = (isSnooze || isDemo()) ? 'none' : '';
    document.getElementById('rem-sms').checked = !!wantsSms;
    window.paintTenantSendNotices?.(smsGroup); // TASK-10027 muted-tenant UI hints
    document.getElementById('rem-error').textContent = '';
    document.getElementById('rem-save').textContent = isSnooze ? 'Snooze' : 'Save';
    m.classList.remove('hidden');
    setTimeout(() => { if (!isSnooze) document.getElementById('rem-title')?.focus(); }, 30);
  }

  function closeModal() {
    const m = document.getElementById('reminder-modal');
    if (m) m.classList.add('hidden');
    S.modal = null;
  }

  function openCreate(type, id, label) {
    if (!canUseType(type)) { toast('Your login does not include the module for this record.', 'error'); return; }
    const def = RT().quickPick('tomorrow', Date.now(), zone());
    openModal({ mode: 'create', type, id: String(id), label: label || '' }, '', def, !isDemo());
  }
  function openEdit(r) {
    openModal({ mode: 'edit', type: r.type, id: r.attachedId, label: r.label, reminderId: r.id, rawDueMs: r.dueMs, origDueMs: RT().fromInputs(RT().toInputs(r.dueMs, zone()).date, RT().toInputs(r.dueMs, zone()).time, zone()) }, r.title, r.dueMs, r.wantsSms);
  }
  function openSnooze(r) {
    const def = RT().quickPick('in1h', Date.now(), zone());
    openModal({ mode: 'snooze', type: r.type, id: r.attachedId, label: r.label, reminderId: r.id }, r.title, def, r.wantsSms);
  }

  async function save() {
    const st = S.modal;
    if (!st || S.busy) return;
    const errEl = document.getElementById('rem-error');
    errEl.textContent = '';
    const title = String(document.getElementById('rem-title').value || '').trim();
    const dueAtMs = RT().fromInputs(document.getElementById('rem-date').value, document.getElementById('rem-time').value, zone());
    const wantsSms = !isDemo() && document.getElementById('rem-sms').checked;
    if (st.mode !== 'snooze' && !title) { errEl.textContent = 'Say what the reminder is for.'; return; }
    // (TASK-10027's untick-to-save guard for a no-contact company whose texts the
    // server did not stop is gone: with TASK-10025 the server refuses every such text.)
    if (!Number.isFinite(dueAtMs)) { errEl.textContent = 'Pick a date and time.'; return; }
    // An overdue reminder may keep its time while its title is edited (server agrees).
    const keptTime = st.mode === 'edit' && dueAtMs === st.origDueMs;
    if (!keptTime && dueAtMs < Date.now() - 60000) { errEl.textContent = 'That time has already passed. Pick a time in the future.'; return; }
    const btn = document.getElementById('rem-save');
    S.busy = true;
    btn.disabled = true;
    try {
      const when = RT().formatDue(dueAtMs, zone(), Date.now());
      if (st.mode === 'create') {
        const res = await call({ op: 'create', attachedType: st.type, attachedId: st.id, title, dueAtMs, wantsSms });
        let msg = `Reminder set for ${when}.`;
        // TASK-10027 — one delivery sentence, never two that disagree (COD r1).
        const _sm = window.getTenantSendMode?.() || { mode: 'live' };
        if (wantsSms && res.noPhone) msg += ' No phone on your profile, so you will get the in-app banner only.';
        else if (wantsSms && _sm.mode === 'live' && res.smsTo) msg += ` We'll text ${res.smsTo}.`;
        // integ/2114 COD (TASK-10025): a HARD no-contact company now has textsHeld too, but its
        // reminder texts are refused for good (opsReminders), never deferred — say so first.
        // on2125: a restore-marked company inside its window only DEFERS them (textsRefused
        // false), so it falls through to the "text waits" sentence.
        else if (wantsSms && _sm.mode === 'noContact' && _sm.textsRefused) msg += ' This company is set to send nothing, so no text will be sent. The in-app reminder still shows.';
        else if (wantsSms && _sm.textsHeld) msg += ' Texts are paused for this company, so the text waits until they are back on.';
        toast(msg, 'success');
      } else if (st.mode === 'snooze') {
        await call({ op: 'snooze', id: st.reminderId, dueAtMs });
        S.dismissed.delete(st.reminderId);
        toast(`Snoozed until ${when}.`, 'success');
      } else {
        await call({ op: 'update', id: st.reminderId, title, dueAtMs: keptTime ? st.rawDueMs : dueAtMs, wantsSms });
        S.dismissed.delete(st.reminderId);
        toast('Reminder updated.', 'success');
      }
      closeModal();
    } catch (e) {
      errEl.textContent = friendly(e);
    } finally {
      S.busy = false;
      btn.disabled = false;
    }
  }

  // --------------------------------------------------------------------------
  // Banner (bottom, so it never stacks on the top CRM / trial banners)
  // --------------------------------------------------------------------------
  function removeBanner() { document.getElementById('reminders-banner')?.remove(); }

  function renderBanner() {
    const due = dueNow().filter((r) => !S.dismissed.has(r.id));
    const shared = sharedDue().filter((n) => !S.dismissed.has(n.id));
    if ((!due.length && !shared.length) || isDriverContext()) { removeBanner(); return; }
    let b = document.getElementById('reminders-banner');
    if (!b) {
      b = document.createElement('div');
      b.id = 'reminders-banner';
      b.className = 'reminders-banner';
      b.setAttribute('role', 'status');
      b.addEventListener('click', onBannerClick);
      document.body.appendChild(b);
    }
    const now = Date.now();
    // TASK-10018 — exactly one thing showing and it is a shared office notice:
    // the whole card (full message, sender, time, actions) right in the banner.
    // Re-rendered only when it changes, so the 30 s tick never steals a click.
    b.classList.toggle('rem-shared-banner', shared.length === 1 && !due.length);
    if (shared.length === 1 && !due.length) {
      const html = `<div class="rem-shared-head"><span class="material-icons rem-bell">sms</span>
          <button type="button" class="rb-icon" data-rb="dismiss" aria-label="Hide"><span class="material-icons">close</span></button></div>
        ${sharedCardHtml(shared[0], now, 'data-rb')}`;
      if (b._remHtml !== html) { b.innerHTML = html; b._remHtml = html; }
      return;
    }
    b._remHtml = null;
    if (shared.length + due.length > 1) {
      const first = shared.length ? shared[0].title : due[0].title;
      b.innerHTML = `<span class="material-icons rem-bell">${shared.length ? 'sms' : 'alarm'}</span>
        <div class="reminders-banner-text"><strong>${shared.length + due.length} ${shared.length && !due.length ? 'office notices' : 'reminders due'}</strong>
          <span>${esc(first)} and more</span></div>
        <div class="reminders-banner-actions">
          <button type="button" class="rb-btn" data-rb="view">View</button>
          <button type="button" class="rb-icon" data-rb="dismiss" aria-label="Hide"><span class="material-icons">close</span></button>
        </div>`;
      return;
    }
    if (due.length === 1) {
      const r = due[0];
      b.innerHTML = `<span class="material-icons rem-bell">alarm</span>
        <div class="reminders-banner-text">
          <strong>${esc(r.title)}</strong>
          <span>${esc(r.label || TYPE_NOUN[r.type])} · ${esc(RT().formatDue(r.dueMs, zone(), now))}</span>
        </div>
        <div class="reminders-banner-actions">
          <button type="button" class="rb-btn" data-rb="snooze" data-rem-id="${esc(r.id)}">Snooze 1h</button>
          <button type="button" class="rb-btn" data-rb="done" data-rem-id="${esc(r.id)}">Done</button>
          <button type="button" class="rb-icon" data-rb="dismiss" aria-label="Hide"><span class="material-icons">close</span></button>
        </div>`;
    }
  }

  function onBannerClick(ev) {
    const btn = ev.target.closest('[data-rb]');
    if (!btn) return;
    const kind = btn.getAttribute('data-rb');
    if (kind === 'dismiss') {
      dueNow().forEach((r) => S.dismissed.add(r.id));
      sharedDue().forEach((n) => S.dismissed.add(n.id));
      removeBanner();
      return;
    }
    if (kind === 'view') { openMyReminders(); return; }
    if (kind.startsWith('shared-')) { onSharedAction(kind, btn.getAttribute('data-rem-id')); return; }
    const r = find(btn.getAttribute('data-rem-id'));
    if (!r) return;
    if (kind === 'snooze') snoozeTo(r, RT().quickPick('in1h', Date.now(), zone()));
    else if (kind === 'done') complete(r);
  }

  // --------------------------------------------------------------------------
  // "My reminders" list (TASK-9569: opened from a bell, the banner or Settings —
  // the sidebar / More-menu entry is gone). `filter` = one record's reminders.
  // --------------------------------------------------------------------------
  function openMyReminders(filter) {
    start();
    S.listFilter = filter && TYPE_MODULES[filter.type] && filter.id
      ? { type: filter.type, id: String(filter.id), label: String(filter.label || '') }
      : null;
    let m = document.getElementById('my-reminders-modal');
    if (!m) {
      m = document.createElement('div');
      m.id = 'my-reminders-modal';
      m.className = 'modal hidden';
      m.setAttribute('role', 'dialog');
      m.setAttribute('aria-modal', 'true');
      m.setAttribute('aria-labelledby', 'my-reminders-title');
      m.innerHTML = `<div class="modal-content rem-my-content">
        <div class="modal-header">
          <h3 id="my-reminders-title">My reminders</h3>
          <button type="button" class="btn-icon" data-rem-close aria-label="Close"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body"><div id="my-reminders-list"></div></div>
      </div>`;
      document.body.appendChild(m);
      m.addEventListener('click', (ev) => {
        if (ev.target === m || ev.target.closest('[data-rem-close]')) { m.classList.add('hidden'); S.listFilter = null; return; }
        onListClick(ev);
      });
    }
    renderMyList();
    m.classList.remove('hidden');
  }

  function openRecordReminders(type, id, label) {
    if (!canUseType(type)) { toast('Your login does not include the module for this record.', 'error'); return; }
    openMyReminders({ type, id, label });
  }

  function renderMyList() {
    const el = document.getElementById('my-reminders-list');
    if (!el) return;
    const f = S.listFilter;
    const everything = all();
    const items = f ? everything.filter((r) => r.type === f.type && r.attachedId === f.id) : everything;
    const now = Date.now();
    const titleEl = document.getElementById('my-reminders-title');
    if (titleEl) titleEl.textContent = f ? `Reminders — ${f.label || TYPE_NOUN[f.type]}` : 'My reminders';
    if (f) {
      el.innerHTML = `<div class="rem-filter-head">
          <button type="button" class="btn btn-sm btn-primary rem-add-btn" data-rem-action="add-filtered">
            <span class="material-icons">add_alarm</span> Add reminder
          </button>
        </div>
        ${items.length
          ? `<div class="rem-list">${items.map((r) => itemHtml(r, now, false)).join('')}</div>`
          : `<div class="rem-empty">No open reminders on this ${TYPE_NOUN[f.type]}. Only you see your reminders.</div>`}
        <button type="button" class="rem-link-btn" data-rem-action="show-all">See all my reminders (${everything.length})</button>`;
      return;
    }
    // TASK-10018 — open office notices first (whole message + actions).
    const sharedHtml = S.shared.length
      ? `<div class="rem-shared-list"><div class="rem-shared-list-head">Office notices — shared with everyone who has Dispatch</div>${S.shared.map((n) => {
        const snoozed = n.snoozeMs > now ? `<div class="rem-shared-snoozed">Snoozed for you until ${esc(RT().formatDue(n.snoozeMs, zone(), now))}</div>` : '';
        return `<div class="rem-shared-in-list">${snoozed}${sharedCardHtml(n, now, 'data-rem-action')}</div>`;
      }).join('')}</div>`
      : '';
    if (sharedHtml && !items.length) { el.innerHTML = sharedHtml; return; }
    el.innerHTML = sharedHtml + (items.length
      ? `<div class="rem-list">${items.map((r) => itemHtml(r, now, true)).join('')}</div>
         <div class="rem-hint" style="margin-top:10px;">To add one, tap the <span class="material-icons rem-inline-icon">notifications_none</span> bell on a person, vehicle or job.</div>`
      : `<div class="rem-empty">No open reminders.<br>Tap the <span class="material-icons rem-inline-icon">notifications_none</span> bell on a person, vehicle or job card to set one — you get a banner here and, if you want, a text when it's due.</div>`);
  }

  // --------------------------------------------------------------------------
  // TASK-9569 — bells on cards (one shared component for every host)
  // --------------------------------------------------------------------------
  // Hosts drop `bellHtml(type, id, label, opts)` into a card. It is always a
  // SLOT (an empty span when the login cannot use reminders on that type, the
  // bells are hidden, or the listeners have not loaded yet) so refreshBells()
  // can fill or empty it in place when the data / access / pref lands — the
  // host never has to re-render. The button's onclick reads only `this`, so no
  // record text is ever interpolated into JavaScript (escJsAttr not needed).
  // opts.compact      — icon only (narrow week-calendar cards)
  // opts.onlyWhenSet  — show nothing until the record has an open reminder
  function bellHtml(type, id, label, opts) {
    if (!TYPE_MODULES[type] || id == null || id === '') return '';
    const o = opts || {};
    const flags = `${o.compact ? ' data-rem-compact="1"' : ''}${o.onlyWhenSet ? ' data-rem-only-set="1"' : ''}`;
    const slot = { type, id: String(id), label: String(label || ''), compact: !!o.compact, onlyWhenSet: !!o.onlyWhenSet };
    return `<span class="rem-bell-slot" data-rem-bell="${esc(type)}" data-rem-bell-id="${esc(String(id))}" data-rem-bell-label="${esc(String(label || ''))}"${flags}>${bellInner(slot)}</span>`;
  }

  // Short chip text: "Due" / "3 PM" (today) / "Tmrw" / "Oct 20".
  function shortDue(ms) {
    const now = Date.now();
    if (!Number.isFinite(ms)) return '';
    if (ms <= now) return 'Due';
    const s = RT().formatDue(ms, zone(), now);
    if (/^Today /.test(s)) return s.slice(6).replace(':00 ', ' ');
    if (/^Tomorrow /.test(s)) return 'Tmrw';
    const m = s.match(/^\w+, (\w+ \d+)/);
    return m ? m[1] : s;
  }

  function bellInner(slot) {
    if (S.bellsHidden || !RT() || isDriverContext() || !canUseType(slot.type)) return '';
    const items = (S.byType[slot.type] || []).filter((r) => r.attachedId === slot.id);
    if (!items.length && slot.onlyWhenSet) return '';
    const noun = TYPE_NOUN[slot.type];
    const click = 'event.stopPropagation();event.preventDefault();window.Reminders&&window.Reminders.onBell(this)';
    if (!items.length) {
      return `<button type="button" class="rem-bell-btn" onclick="${click}" title="Set a reminder on this ${noun}" aria-label="Set a reminder on this ${noun}"><span class="material-icons" aria-hidden="true">notifications_none</span></button>`;
    }
    const first = items.reduce((a, b) => (b.dueMs < a.dueMs ? b : a));
    const overdue = first.dueMs <= Date.now();
    const more = items.length > 1 ? ` +${items.length - 1}` : '';
    const aria = `${items.length} reminder${items.length > 1 ? 's' : ''} on this ${noun}, next ${RT().formatDue(first.dueMs, zone(), Date.now())}`;
    return `<button type="button" class="rem-bell-btn rem-bell-set${overdue ? ' rem-bell-overdue' : ''}" onclick="${click}" title="${esc(aria)}" aria-label="${esc(aria)}"><span class="material-icons" aria-hidden="true">${overdue ? 'notifications_active' : 'notifications'}</span>${slot.compact ? '' : `<span class="rem-bell-due">${esc(shortDue(first.dueMs))}${esc(more)}</span>`}</button>`;
  }

  function slotFromEl(el) {
    const s = el && el.closest ? el.closest('.rem-bell-slot') : null;
    if (!s) return null;
    return {
      type: s.getAttribute('data-rem-bell'),
      id: s.getAttribute('data-rem-bell-id') || '',
      label: s.getAttribute('data-rem-bell-label') || '',
      compact: s.getAttribute('data-rem-compact') === '1',
      onlyWhenSet: s.getAttribute('data-rem-only-set') === '1',
    };
  }

  function refreshBells(root) {
    const scope = root || document;
    scope.querySelectorAll('.rem-bell-slot').forEach((el) => {
      const slot = slotFromEl(el);
      if (!slot || !TYPE_MODULES[slot.type]) return;
      const html = bellInner(slot);
      // Compare against what WE last wrote (the browser re-serializes
      // innerHTML, so comparing to it would rewrite every slot every time).
      if (el._remHtml === html) return;
      if (el._remHtml === undefined && el.innerHTML === html) { el._remHtml = html; return; }
      el.innerHTML = html;
      el._remHtml = html;
    });
  }

  function onBell(el) {
    const slot = slotFromEl(el);
    if (!slot || !TYPE_MODULES[slot.type] || !slot.id) return;
    const has = (S.byType[slot.type] || []).some((r) => r.attachedId === slot.id);
    if (has) openRecordReminders(slot.type, slot.id, slot.label);
    else openCreate(slot.type, slot.id, slot.label);
  }

  // --------------------------------------------------------------------------
  // TASK-9569 — per-login "Show reminder bells" (Settings)
  // --------------------------------------------------------------------------
  // Stored on the caller's OWN users/{uid} doc (self read/update in
  // firestore.rules; tenantId / partnerOwnTenant untouched, so no rules
  // change). UPDATE, never create: a users doc with no tenantId makes the next
  // sign-in stop at "Account setup incomplete", so a login without one (e.g. a
  // compliance partner in a client company) keeps the pref in localStorage
  // only — as do demo / anonymous sessions. Hiding is presentational:
  // reminders, the banner and texts are untouched.
  function prefLsKey(uid) { return `dd_reminder_bells_hidden_${uid}`; }
  function canStorePref(user) { return !!(user && !user.isAnonymous && !isDemo()); }

  function loadPref(user) {
    if (!user || S.prefKey === user.uid) return;
    S.prefKey = user.uid;
    try { S.bellsHidden = localStorage.getItem(prefLsKey(user.uid)) === '1'; } catch (_) { S.bellsHidden = false; }
    if (!canStorePref(user)) return;
    const rev = S.prefRev;
    getDoc(doc(db, 'users', user.uid)).then((snap) => {
      // COD (Code Reviewer): a slow read must not undo a newer tick of the box.
      if (S.prefKey !== user.uid || S.prefRev !== rev || !snap.exists()) return;
      const hidden = (snap.data() || {}).reminderBellsHidden === true;
      S.bellsHidden = hidden;
      try { localStorage.setItem(prefLsKey(user.uid), hidden ? '1' : '0'); } catch (_) { /* ignore */ }
      refreshBells();
      paintSettingsRow();
    }).catch((e) => console.warn('[Reminders] bell pref read failed (kept the cached value):', e?.code || e?.message || e));
  }

  async function setBellsHidden(hidden) {
    const user = auth.currentUser;
    const next = !!hidden;
    const prev = S.bellsHidden;
    S.prefRev += 1;
    S.bellsHidden = next;
    refreshBells();
    paintSettingsRow();
    if (!user) return true;
    try { localStorage.setItem(prefLsKey(user.uid), next ? '1' : '0'); } catch (_) { /* ignore */ }
    if (!canStorePref(user)) return true;
    try {
      await updateDoc(doc(db, 'users', user.uid), { reminderBellsHidden: next });
      return true;
    } catch (e) {
      // No profile doc to update: this device keeps it (localStorage, above).
      if (e?.code === 'not-found') return true;
      console.warn('[Reminders] bell pref save failed:', e?.code || e?.message || e);
      S.bellsHidden = prev;
      try { localStorage.setItem(prefLsKey(user.uid), prev ? '1' : '0'); } catch (_) { /* ignore */ }
      refreshBells();
      paintSettingsRow();
      toast('Could not save that setting. Try again.', 'error');
      return false;
    }
  }

  // Settings modal row (#settings-reminder-bells-group in index.html). Shown
  // only to a login that can hold a reminder; app.js repaints it on open.
  function paintSettingsRow() {
    const g = document.getElementById('settings-reminder-bells-group');
    if (!g) return;
    const usable = !isDriverContext() && Object.keys(TYPE_MODULES).some(canUseType);
    g.style.display = usable ? '' : 'none';
    if (!usable) return;
    const cb = document.getElementById('settings-reminder-bells');
    if (cb) {
      cb.checked = !S.bellsHidden;
      if (!cb._remBound) {
        cb._remBound = true;
        cb.addEventListener('change', () => { setBellsHidden(!cb.checked); });
      }
    }
    const btn = document.getElementById('settings-my-reminders-btn');
    if (btn) {
      const n = all().length;
      const lbl = btn.querySelector('.rem-settings-count');
      if (lbl) lbl.textContent = n ? `(${n})` : '';
      if (!btn._remBound) {
        btn._remBound = true;
        btn.addEventListener('click', () => openMyReminders());
      }
    }
  }

  // --------------------------------------------------------------------------
  // Styles (self-contained: no styles.css buster to keep in sync)
  // --------------------------------------------------------------------------
  function injectCss() {
    if (document.getElementById('reminders-css')) return;
    const s = document.createElement('style');
    s.id = 'reminders-css';
    s.textContent = `
      .rem-section{border:1px solid var(--border-light);border-radius:var(--radius-md);padding:10px 12px;margin-top:12px;background:var(--bg-card)}
      .rem-section-head{display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap}
      .rem-section-title{display:flex;align-items:center;gap:6px;font-weight:600;color:var(--text-primary)}
      .rem-section-title .material-icons{font-size:18px;color:var(--brand-blue)}
      .rem-add-btn{display:inline-flex;align-items:center;gap:4px}
      .rem-add-btn .material-icons{font-size:16px}
      .rem-list{display:flex;flex-direction:column;gap:8px;margin-top:8px}
      .rem-empty{color:var(--text-secondary);font-size:0.85rem;padding:6px 0}
      .rem-item{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;padding:8px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);background:var(--bg-primary)}
      .rem-item.rem-overdue{border-color:#FCD34D;background:#FFFBEB}
      .rem-item-main{min-width:0;flex:1}
      .rem-item-title{font-weight:600;color:var(--text-primary);overflow-wrap:anywhere}
      .rem-about-line,.rem-item-meta{display:flex;align-items:center;gap:4px;flex-wrap:wrap;font-size:0.8rem;color:var(--text-secondary);margin-top:2px}
      .rem-about-line .material-icons,.rem-item-meta .material-icons{font-size:14px}
      .rem-sms-chip{display:inline-flex;align-items:center;gap:3px;margin-left:6px;padding:1px 8px;border-radius:10px;background:var(--bg-elevated);color:var(--text-secondary)}
      .rem-item-actions{display:flex;gap:4px;flex-shrink:0}
      .rem-item-actions .btn{padding:4px 6px;min-width:32px;min-height:32px;display:inline-flex;align-items:center;justify-content:center}
      .rem-item-actions .material-icons{font-size:16px}
      #reminder-modal,#my-reminders-modal{z-index:1060}
      .rem-modal-content{max-width:460px}
      .rem-my-content{max-width:560px}
      #reminder-modal .modal-content,#my-reminders-modal .modal-content{display:flex;flex-direction:column}
      #reminder-modal .modal-body,#my-reminders-modal .modal-body{overflow-y:auto}
      .rem-modal-about{display:flex;align-items:center;gap:6px;color:var(--text-primary);font-weight:600;margin-bottom:10px;flex-wrap:wrap}
      .rem-modal-about .material-icons{font-size:18px;color:var(--brand-blue)}
      .rem-modal-about-title{font-weight:400;color:var(--text-secondary)}
      .rem-quick{display:flex;flex-wrap:wrap;gap:6px;margin:4px 0 10px}
      .rem-when{display:grid;grid-template-columns:1fr 1fr;gap:10px}
      .rem-hint{font-size:0.8rem;color:var(--text-secondary);margin-top:2px}
      .rem-pii-warning{margin-top:6px;color:var(--text-primary)}
      .rem-pii-warning.rem-pii-hit{color:var(--danger);font-weight:600}
      .rem-check{display:flex;align-items:center;gap:8px;margin-top:12px;font-weight:600;color:var(--text-primary);cursor:pointer}
      .rem-error{color:var(--status-danger);font-size:0.85rem;margin-top:8px;min-height:1em}
      .reminders-banner{position:fixed;left:50%;transform:translateX(-50%);bottom:calc(16px + env(safe-area-inset-bottom));z-index:1001;
        display:flex;align-items:center;gap:12px;max-width:min(640px,calc(100vw - 24px));width:max-content;
        background:var(--brand-navy);color:#fff;padding:10px 12px 10px 16px;border-radius:var(--radius-lg);box-shadow:var(--shadow-xl)}
      .reminders-banner .rem-bell{color:#FBBF24;font-size:22px;flex-shrink:0}
      .reminders-banner-text{display:flex;flex-direction:column;min-width:0}
      .reminders-banner-text strong{color:#fff;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
      .reminders-banner-text span{color:#CBD5E1;font-size:0.8rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
      .reminders-banner-actions{display:flex;align-items:center;gap:6px;flex-shrink:0}
      .reminders-banner .rb-btn{background:rgba(255,255,255,0.14);color:#fff;border:1px solid rgba(255,255,255,0.3);border-radius:var(--radius-sm);padding:6px 12px;font-weight:600;cursor:pointer;min-height:36px}
      .reminders-banner .rb-btn:hover{background:rgba(255,255,255,0.26)}
      .reminders-banner .rb-icon{background:transparent;border:none;color:#fff;cursor:pointer;display:flex;align-items:center;padding:4px;min-height:36px}
      .rem-bell-slot{display:inline-flex;vertical-align:middle}
      .rem-bell-slot:empty{display:none}
      .rem-bell-btn{display:inline-flex;align-items:center;justify-content:center;gap:3px;min-width:32px;min-height:32px;margin:-6px 2px -6px 4px;padding:2px 6px;border:1px solid transparent;border-radius:16px;background:transparent;color:var(--text-secondary);font:inherit;font-size:0.75rem;font-weight:600;line-height:1;cursor:pointer}
      .rem-bell-btn .material-icons{font-size:18px}
      .rem-bell-btn:hover,.rem-bell-btn:focus-visible{background:var(--bg-elevated);color:var(--text-primary);outline:none;border-color:var(--border-light)}
      .rem-bell-btn.rem-bell-set{background:#FEF3C7;border-color:#FCD34D;color:#92400E}
      .rem-bell-btn.rem-bell-set .material-icons{color:#D97706}
      .rem-bell-btn.rem-bell-overdue{background:#FDE68A;border-color:#F59E0B;color:#78350F}
      .rem-bell-due{white-space:nowrap}
      [data-rem-compact="1"] .rem-bell-btn{min-width:22px;min-height:22px;margin:-3px 0 -3px 2px;padding:1px 3px}
      [data-rem-compact="1"] .rem-bell-btn .material-icons{font-size:14px}
      .rem-filter-head{display:flex;justify-content:flex-start;margin-bottom:4px}
      .rem-link-btn{display:block;margin-top:12px;padding:8px 0;border:none;background:none;color:var(--brand-blue);font-weight:600;cursor:pointer;text-align:left;min-height:36px}
      .rem-inline-icon{font-size:15px;vertical-align:-3px}
      .reminders-banner.rem-shared-banner{flex-direction:column;align-items:stretch;gap:6px;width:min(640px,calc(100vw - 24px));padding:10px 14px 12px}
      .rem-shared-head{display:flex;align-items:center;justify-content:space-between}
      .rem-shared-card{display:flex;flex-direction:column;gap:6px;min-width:0}
      .rem-shared-title{font-weight:700;overflow-wrap:anywhere}
      .rem-shared-msg{white-space:pre-wrap;overflow-wrap:anywhere;max-height:30vh;overflow-y:auto;padding:8px 10px;border-radius:var(--radius-sm);font-size:0.9rem;line-height:1.35}
      .rem-shared-meta{font-size:0.8rem}
      .rem-shared-actions{display:flex;flex-wrap:wrap;gap:6px;margin-top:2px}
      .reminders-banner .rem-shared-title{color:#fff}
      .reminders-banner .rem-shared-msg{background:rgba(255,255,255,0.10);color:#fff}
      .reminders-banner .rem-shared-meta{color:#CBD5E1}
      .reminders-banner .rem-shared-primary{background:var(--brand-blue);border-color:var(--brand-blue)}
      .rem-shared-list{display:flex;flex-direction:column;gap:8px;margin-bottom:14px}
      .rem-shared-list-head{font-weight:600;color:var(--text-primary);font-size:0.85rem}
      .rem-shared-in-list{border:1px solid #FCD34D;background:#FFFBEB;border-radius:var(--radius-sm);padding:10px 12px}
      .rem-shared-in-list .rem-shared-title{color:var(--text-primary)}
      .rem-shared-in-list .rem-shared-msg{background:var(--bg-card);border:1px solid var(--border-light);color:var(--text-primary)}
      .rem-shared-in-list .rem-shared-meta,.rem-shared-snoozed{color:var(--text-secondary);font-size:0.8rem}
      .rem-shared-in-list .rb-btn{background:var(--bg-card);color:var(--text-primary);border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:6px 12px;font-weight:600;cursor:pointer;min-height:36px}
      .rem-shared-in-list .rem-shared-primary{background:var(--brand-blue);border-color:var(--brand-blue);color:#fff}
      #rem-thread-modal{z-index:1070}
      .rem-thread-row{padding:8px 10px;border-radius:var(--radius-sm);margin-bottom:6px;max-width:85%}
      .rem-thread-in{background:var(--bg-elevated);color:var(--text-primary)}
      .rem-thread-out{background:#EFF6FF;color:var(--text-primary);margin-left:auto}
      .rem-thread-who{font-size:0.75rem;color:var(--text-secondary);margin-bottom:2px}
      .rem-thread-text{white-space:pre-wrap;overflow-wrap:anywhere}
      @media (max-width:768px){
        .reminders-banner{bottom:calc(80px + env(safe-area-inset-bottom));width:calc(100vw - 24px)}
        .rem-item{flex-direction:column}
        .rem-item-actions{align-self:flex-end}
        .rem-when{grid-template-columns:1fr}
        .rem-bell-btn{min-width:36px;min-height:36px;margin:-8px 2px -8px 4px}
      }`;
    document.head.appendChild(s);
  }

  window.Reminders = {
    start,
    stop,
    mount,
    mountAppend,
    unmount,
    openCreate,
    openMyReminders: () => openMyReminders(),
    openRecordReminders,
    closeModal,
    canUseType,
    // TASK-9569 — bells on cards + per-login show/hide
    bellHtml,
    refreshBells,
    onBell,
    setBellsHidden,
    bellsHidden: () => S.bellsHidden === true,
    paintSettingsRow,
    // test hooks (read-only views of state)
    _state: () => ({ types: S.types.slice(), count: all().length, due: dueNow().length, shared: S.shared.length, sharedDue: sharedDue().length }),
    canMakeJob, // TASK-10018

  };
})();
